import PropTypes from 'prop-types';
import { BsExclamationTriangle } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

/**
 * Confirmation dialog for an action the user cannot take back — or, with `confirmVariant="solid"`
 * and a matching `subtitle`, for a non-destructive action that affects many rows at once.
 *
 * Pass `icon` to replace the warning triangle.
 */
export const ConfirmModal = ({
	isOpen,
	onClose,
	onConfirm,
	title = 'Löschen bestätigen',
	message = 'Sind Sie sicher, dass Sie dieses Element löschen möchten?',
	confirmText = 'Löschen',
	cancelText = 'Abbrechen',
	isLoading = false,
	subtitle = 'Diese Aktion kann nicht rückgängig gemacht werden.',
	icon: Icon = BsExclamationTriangle,
	confirmVariant = 'danger',
}) => {
	// Await onConfirm so `isLoading` can render while it runs, and keep the modal open when it
	// rejects — closing on a failed action hides the failure behind an already-dismissed dialog.
	// Handlers that swallow their own errors resolve normally and still close.
	// The rejection itself is not rethrown: the global MutationCache.onError already surfaced it,
	// and an async onClick would otherwise leave an unhandled rejection behind.
	const handleConfirm = async () => {
		try {
			await onConfirm();
		} catch {
			return;
		}
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="s" closeOnBackdrop={!isLoading}>
			<Panel.Header icon={Icon} onClose={isLoading ? undefined : onClose}>
				{title}
			</Panel.Header>
			<Panel.Body>
				<div className="text-md text-text leading-relaxed">{message}</div>
				{subtitle !== null && <p className="text-sm text-bgBase-800 mt-2">{subtitle}</p>}
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
					{cancelText}
				</Button>
				<Button
					type="button"
					onClick={handleConfirm}
					variant={confirmVariant}
					loading={isLoading}
					disabled={isLoading}
				>
					{confirmText}
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

ConfirmModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	title: PropTypes.string,
	message: PropTypes.node,
	confirmText: PropTypes.string,
	cancelText: PropTypes.string,
	isLoading: PropTypes.bool,
	subtitle: PropTypes.node,
	icon: PropTypes.elementType,
	confirmVariant: PropTypes.oneOf(['danger', 'solid']),
};

export default ConfirmModal;
